<template>
  <div class="message-conversion-report">
    <a-tabs v-model:activeKey="activeMessageType" class="message-conversion-sub-tabs">
      <a-tab-pane key="certificate" tab="📬 인증서 알림톡">
        <a-table
          :columns="certificateColumns"
          :data-source="filterByMessageType('certificate')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <span
                v-if="record.messageType === 'certificate'"
                class="link-text"
                @click="navigateToCertificateList(record.date)"
              >
                {{ formatNumber(record.sent) }}
              </span>
              <span v-else>{{ formatNumber(record.sent) }}</span>
            </template>
            <template v-else-if="column.key === 'clicks'">
              <span>{{ formatClicks(record.uniqueClicks, record.clicks) }}</span>
            </template>
            <template v-else-if="column.key === 'clickRate'">
              <span>{{ formatPercentage(record.clickRate) }}</span>
            </template>
            <template v-else-if="column.key === 'eventPageVisits'">
              <span>{{ formatNumber(record.eventPageVisits) }}</span>
            </template>
            <template v-else-if="column.key === 'shares'">
              <span>{{ getAdjustedShares(record.shares, record.date, record.messageType) }}</span>
            </template>
            <template v-else-if="column.key === 'referredQuizParticipants'">
              <span>{{ formatNumber(record.referredQuizParticipants || 0) }}</span>
            </template>
            <template v-else-if="column.key === 'couponIssuances'">
              <span>{{ formatNumber(record.couponIssuances || 0) }}</span>
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('certificate'), 'sent')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ calculateSumClicks(filterByMessageType('certificate')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ calculateClickRateFromSum(filterByMessageType('certificate')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('certificate'), 'eventPageVisits')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSharesSum(filterByMessageType('certificate'))) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="review" tab="✍️ 후기 알림톡">
        <a-table
          :columns="reviewColumns"
          :data-source="filterByMessageType('review')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <span
                v-if="record.messageType === 'certificate'"
                class="link-text"
                @click="navigateToCertificateList(record.date)"
              >
                {{ formatNumber(record.sent) }}
              </span>
              <span v-else>{{ formatNumber(record.sent) }}</span>
            </template>
            <template v-else-if="column.key === 'clicks'">
              <span>{{ formatClicks(record.uniqueClicks, record.clicks) }}</span>
            </template>
            <template v-else-if="column.key === 'clickRate'">
              <span>{{ formatPercentage(record.clickRate) }}</span>
            </template>
            <template v-else-if="column.key === 'reviewSubmissions'">
              <span>{{ displayValue(record.reviewSubmissions, record.messageType, 'reviewSubmissions') }}</span>
            </template>
            <template v-else-if="column.key === 'shares'">
              <span>{{ getAdjustedShares(record.shares, record.date, record.messageType) }}</span>
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('review'), 'sent')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ calculateSumClicks(filterByMessageType('review')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ calculateClickRateFromSum(filterByMessageType('review')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('review'), 'reviewSubmissions')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSharesSum(filterByMessageType('review'))) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="friend" tab="🏥 병원예약 유도 친구톡">
        <a-table
          :columns="friendColumns"
          :data-source="filterByMessageType('friend')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <span
                v-if="record.messageType === 'certificate'"
                class="link-text"
                @click="navigateToCertificateList(record.date)"
              >
                {{ formatNumber(record.sent) }}
              </span>
              <span v-else>{{ formatNumber(record.sent) }}</span>
            </template>
            <!-- 클릭수 컬럼 주석 처리
            <template v-else-if="column.key === 'clicks'">
              <span>{{ formatClicks(record.uniqueClicks, record.clicks) }}</span>
            </template>
            -->
            <!-- 클릭률 컬럼 주석 처리
            <template v-else-if="column.key === 'clickRate'">
              <span>{{ formatPercentage(record.clickRate) }}</span>
            </template>
            -->
            <template v-else-if="column.key === 'quizParticipants'">
              <span>{{ displayValue(record.quizParticipants, record.messageType, 'quizParticipants') }}</span>
            </template>
            <template v-else-if="column.key === 'hospitalSelections'">
              <span>{{ getAdjustedHospitalSelections(record.hospitalSelections, record.date, record.messageType) }}</span>
            </template>
            <template v-else-if="column.key === 'customerInfoInputs'">
              <span>{{ getAdjustedCustomerInfoInputs(record.customerInfoInputs, record.date, record.messageType) }}</span>
            </template>
            <template v-else-if="column.key === 'reservations'">
              <span :class="{ 'reservation-highlight': record.reservations > 0 }">
                {{ formatNumber(record.reservations) }}
              </span>
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('friend'), 'sent')) }}</strong>
                </a-table-summary-cell>
                <!-- 클릭수 컬럼 주석 처리
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ calculateSumClicks(filterByMessageType('friend')) }}</strong>
                </a-table-summary-cell>
                -->
                <!-- 클릭률 컬럼 주석 처리
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ calculateClickRateFromSum(filterByMessageType('friend')) }}</strong>
                </a-table-summary-cell>
                -->
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('friend'), 'quizParticipants')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ formatNumber(calculateAdjustedHospitalSelectionsSum(filterByMessageType('friend'))) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateAdjustedCustomerInfoInputsSum(filterByMessageType('friend'))) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateSum(filterByMessageType('friend'), 'reservations')) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { ref, computed, h } from 'vue';
import { useRouter } from 'vue-router';
import { Table, Tabs, Tooltip } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import {
  messageTypeLabels,
  formatNumber,
  formatPercentage,
  formatDate,
  displayValue,
  sortByMessageType,
  formatClicks,
  getDayOfWeek
} from '../utils';

export default {
  name: 'MessageConversionReport',
  components: {
    ATable: Table,
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    ATableSummary: Table.Summary,
    ATableSummaryRow: Table.Summary.Row,
    ATableSummaryCell: Table.Summary.Cell,
  },
  props: {
    reportData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const router = useRouter();
    const loading = ref(false);
    const activeMessageType = ref('certificate');

    // 공통 컬럼 정의
    const baseColumns = [
      {
        title: '날짜',
        dataIndex: 'date',
        key: 'date',
        width: 100,
        fixed: 'left',
      },
      {
        title: '메시지유형',
        dataIndex: 'messageType',
        key: 'messageType',
        width: 130,
      },
      {
        title: '발송건수',
        dataIndex: 'sent',
        key: 'sent',
        width: 100,
        align: 'right',
      },
      {
        title: '알림톡클릭수(중복건수)',
        dataIndex: 'clicks',
        key: 'clicks',
        width: 170,
        align: 'right',
      },
      {
        title: '클릭률',
        dataIndex: 'clickRate',
        key: 'clickRate',
        width: 100,
        align: 'right',
      },
      {
        title: '이벤트페이지유입수',
        dataIndex: 'eventPageVisits',
        key: 'eventPageVisits',
        width: 150,
        align: 'right',
      },
    ];

    // 인증서/후기 알림톡 컬럼 (후기 작성 퍼널)
    const certificateColumns = [
      ...baseColumns,
      {
        title: '공유수',
        dataIndex: 'shares',
        key: 'shares',
        width: 80,
        align: 'right',
      },
    ];

    // 후기 알림톡 컬럼 (후기 작성 퍼널)
    // eventPageVisits 제외한 baseColumns 사용
    const reviewBaseColumns = baseColumns.filter(col => col.key !== 'eventPageVisits');
    const reviewColumns = [
      ...reviewBaseColumns,
      {
        title: '후기작성수',
        dataIndex: 'reviewSubmissions',
        key: 'reviewSubmissions',
        width: 110,
        align: 'right',
      },
      {
        title: '공유수',
        dataIndex: 'shares',
        key: 'shares',
        width: 80,
        align: 'right',
      },
    ];

    // 친구톡 컬럼 (병원 예약 퍼널)
    // baseColumns의 clicks, clickRate, eventPageVisits 컬럼 제외
    const friendBaseColumns = baseColumns
      .filter(col => col.key !== 'eventPageVisits' && col.key !== 'clickRate' && col.key !== 'clicks')
      .map(col => {
        // clicks 컬럼 제외했으므로 해당 map 로직 주석 처리
        // if (col.key === 'clicks') {
        //   return { ...col, title: '친구톡클릭수(중복건수)' };
        // }
        if (col.key === 'sent') {
          return {
            ...col,
            title: () => h('span', [
              '발송건수 ',
              h(Tooltip, {
                title: '친구톡은 서티라이프 카카오 채널을 팔로우한 사용자에게만 발송됩니다. 따라서 실제 발송 건수와 수신 건수 간에 차이가 발생할 수 있습니다.'
              }, () => h(QuestionCircleOutlined, { style: { color: '#1890ff', cursor: 'pointer' } }))
            ])
          };
        }
        return col;
      });
    const friendColumns = [
      ...friendBaseColumns,
      {
        title: () => h('span', [
          '퀴즈참여수 ',
          h(Tooltip, {
            title: '스마일 환자의 카카오톡 공유하기 및 N차 공유를 통한 퀴즈 이벤트 참여자 수'
          }, () => h(QuestionCircleOutlined, { style: { color: '#1890ff', cursor: 'pointer' } }))
        ]),
        dataIndex: 'quizParticipants',
        key: 'quizParticipants',
        width: 110,
        align: 'right',
      },
      {
        title: '병원선택수',
        dataIndex: 'hospitalSelections',
        key: 'hospitalSelections',
        width: 110,
        align: 'right',
      },
      {
        title: '고객정보입력수',
        dataIndex: 'customerInfoInputs',
        key: 'customerInfoInputs',
        width: 130,
        align: 'right',
      },
      {
        title: '병원예약수',
        dataIndex: 'reservations',
        key: 'reservations',
        width: 110,
        align: 'right',
      },
    ];

    // 전체 데이터 정렬: 날짜 오름차순 -> 메시지 타입 순서
    const processedData = computed(() => {
      if (!props.reportData || props.reportData.length === 0) return [];

      return [...props.reportData].sort((a, b) => {
        const dateCompare = new Date(a.date) - new Date(b.date);
        if (dateCompare !== 0) return dateCompare;
        return sortByMessageType(a, b);
      });
    });

    // 세그먼트별 필터링
    const filterByMessageType = (messageType) => {
      if (!props.reportData || props.reportData.length === 0) return [];

      // messageType 기반 필터링
      if (messageType === 'certificate') {
        // 신규 인증서 발급자
        return processedData.value.filter(item =>
          item.messageType === 'certificate'
        );
      } else if (messageType === 'review') {
        // 기존 인증서 보유자
        return processedData.value.filter(item =>
          item.messageType === 'review'
        );
      } else if (messageType === 'friend') {
        // 인증서 미보유자
        return processedData.value.filter(item =>
          item.messageType === 'friend'
        );
      }

      return [];
    };

    // 인증서 리스트로 이동
    const navigateToCertificateList = (date) => {
      // date가 이미 YYYY-MM-DD 형식이므로 그대로 사용
      router.push({
        path: '/certificate/list',
        query: {
          startDate: date,
          endDate: date,
          prevPage: '/events-v2/report'
        }
      });
    };

    // 날짜 컬럼 색상 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday'; // 일요일
      if (dayOfWeek === 6) return 'date-saturday'; // 토요일
      return '';
    };

    // 병원선택수 보정 함수
    const getAdjustedHospitalSelections = (hospitalSelections, date, messageType) => {
      const baseValue = hospitalSelections || 0;

      // 날짜 파싱 (YYYY-MM-DD 형식)
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1; // 0부터 시작하므로 +1
      const day = dateObj.getDate();

      // friend 타입: 2025년 10월 13일 또는 10월 20일인 경우 +1
      if (messageType === 'friend' && year === 2025 && month === 10 && (day === 13 || day === 20)) {
        return formatNumber(baseValue + 1);
      }

      // 기본값 반환
      return formatNumber(baseValue);
    };

    // 고객정보 입력수 보정 함수
    const getAdjustedCustomerInfoInputs = (customerInfoInputs, date, messageType) => {
      const baseValue = customerInfoInputs || 0;

      // 날짜 파싱 (YYYY-MM-DD 형식)
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1; // 0부터 시작하므로 +1
      const day = dateObj.getDate();

      // friend 타입: 2025년 10월 20일인 경우 +1
      if (messageType === 'friend' && year === 2025 && month === 10 && day === 20) {
        return formatNumber(baseValue + 1);
      }

      // 기본값 반환
      return formatNumber(baseValue);
    };

    // 공유수 보정 함수
    const getAdjustedShares = (shares, date, messageType) => {
      const baseValue = shares || 0;

      // 날짜 파싱 (YYYY-MM-DD 형식)
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1; // 0부터 시작하므로 +1
      const day = dateObj.getDate();

      // certificate 타입이 아니면 보정 없이 원본 반환
      if (messageType !== 'certificate') {
        return displayValue(shares, messageType, 'shares');
      }

      // certificate 타입: 2025년 10월 31일인 경우 +18
      if (year === 2025 && month === 10 && day === 31) {
        return formatNumber(baseValue + 18);
      }

      // certificate 타입: 2025년 9월 19일 ~ 10월 30일 범위인 경우 +5
      const startDate = new Date(2025, 8, 19); // 9월 19일
      const endDate = new Date(2025, 9, 30);   // 10월 30일

      if (dateObj >= startDate && dateObj <= endDate) {
        return formatNumber(baseValue + 5);
      }

      // 범위 밖이면 원본 표시
      return displayValue(shares, messageType, 'shares');
    };

    // 클릭률 포맷팅 (퍼센트 단위로 변환)
    const formatClickRatePercent = (clickRate) => {
      if (clickRate === null || clickRate === undefined) return '-';
      if (typeof clickRate !== 'number') return clickRate;
      // 소수점 값(0.5 등)을 퍼센트로 변환 (50% 등)
      return `${(clickRate * 100).toFixed(2)}%`;
    };

    // 합계 계산 함수
    const calculateSum = (data, field) => {
      if (!data || data.length === 0) return 0;
      return data.reduce((sum, item) => sum + (item[field] || 0), 0);
    };

    // 평균 계산 함수 (비율 필드용 - 소수점 유지)
    const calculateAverage = (data, field) => {
      if (!data || data.length === 0) return 0;
      const sum = calculateSum(data, field);
      return sum / data.length;
    };

    // 클릭수 합계 계산 (uniqueClicks(clicks) 형식)
    const calculateSumClicks = (data) => {
      if (!data || data.length === 0) return '-';
      const totalUnique = calculateSum(data, 'uniqueClicks');
      const totalClicks = calculateSum(data, 'clicks');
      return formatClicks(totalUnique, totalClicks);
    };

    // 클릭수 평균 계산 (uniqueClicks(clicks) 형식) - 건수는 반올림
    const calculateAverageClicks = (data) => {
      if (!data || data.length === 0) return '-';
      const avgUnique = Math.round(calculateAverage(data, 'uniqueClicks'));
      const avgClicks = Math.round(calculateAverage(data, 'clicks'));
      return formatClicks(avgUnique, avgClicks);
    };

    // 합계 기반 클릭률 계산 (합계 클릭수 / 합계 발송건수)
    const calculateClickRateFromSum = (data) => {
      if (!data || data.length === 0) return formatClickRatePercent(0);
      const totalClicks = calculateSum(data, 'uniqueClicks');
      const totalSent = calculateSum(data, 'sent');
      if (totalSent === 0) return formatClickRatePercent(0);
      return formatClickRatePercent(totalClicks / totalSent);
    };

    // 합계 기반 전환율 계산 (합계 예약수 / 합계 발송건수)
    const calculateConversionRateFromSum = (data) => {
      if (!data || data.length === 0) return formatClickRatePercent(0);
      const totalReservations = calculateSum(data, 'reservations');
      const totalSent = calculateSum(data, 'sent');
      if (totalSent === 0) return formatClickRatePercent(0);
      return formatClickRatePercent(totalReservations / totalSent);
    };

    // 병원선택수 합계 계산 (보정값 포함)
    const calculateAdjustedHospitalSelectionsSum = (data) => {
      if (!data || data.length === 0) return 0;

      return data.reduce((sum, item) => {
        const baseValue = item.hospitalSelections || 0;

        // 날짜 파싱
        const dateObj = new Date(item.date);
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();

        // friend 타입: 2025년 10월 13일 또는 10월 20일인 경우 +1
        if (item.messageType === 'friend' && year === 2025 && month === 10 && (day === 13 || day === 20)) {
          return sum + baseValue + 1;
        }

        // 기본값 반환
        return sum + baseValue;
      }, 0);
    };

    // 고객정보 입력수 합계 계산 (보정값 포함)
    const calculateAdjustedCustomerInfoInputsSum = (data) => {
      if (!data || data.length === 0) return 0;

      return data.reduce((sum, item) => {
        const baseValue = item.customerInfoInputs || 0;

        // 날짜 파싱
        const dateObj = new Date(item.date);
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();

        // friend 타입: 2025년 10월 20일인 경우 +1
        if (item.messageType === 'friend' && year === 2025 && month === 10 && day === 20) {
          return sum + baseValue + 1;
        }

        // 기본값 반환
        return sum + baseValue;
      }, 0);
    };

    // 공유수 합계 계산 (보정값 포함)
    const calculateAdjustedSharesSum = (data) => {
      if (!data || data.length === 0) return 0;

      return data.reduce((sum, item) => {
        const baseValue = item.shares || 0;

        // 날짜 파싱
        const dateObj = new Date(item.date);
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();

        // review 타입: 2025년 9월 22일 ~ 10월 31일 범위인 경우 +1
        if (item.messageType === 'review') {
          const reviewStartDate = new Date(2025, 8, 22); // 9월 22일
          const reviewEndDate = new Date(2025, 9, 31);   // 10월 31일

          if (dateObj >= reviewStartDate && dateObj <= reviewEndDate) {
            return sum + baseValue + 1;
          }

          return sum + baseValue;
        }

        // certificate 타입이 아니면 원본값만 합산
        if (item.messageType !== 'certificate') {
          return sum + baseValue;
        }

        // certificate 타입: 2025년 10월 31일인 경우 - 18
        if (year === 2025 && month === 10 && day === 31) {
          return sum + baseValue - 18;
        }

        // certificate 타입: 2025년 9월 19일 ~ 10월 30일 범위인 경우 +5
        const startDate = new Date(2025, 8, 19); // 9월 19일
        const endDate = new Date(2025, 9, 30);   // 10월 30일

        if (dateObj >= startDate && dateObj <= endDate) {
          return sum + baseValue + 6;
        }

        // 범위 밖이면 원본값만
        return sum + baseValue;
      }, 0);
    };

    return {
      loading,
      activeMessageType,
      certificateColumns,
      reviewColumns,
      friendColumns,
      processedData,
      filterByMessageType,
      navigateToCertificateList,
      messageTypeLabels,
      formatNumber,
      formatPercentage,
      formatDate,
      displayValue,
      formatClicks,
      getDateClass,
      getAdjustedHospitalSelections,
      getAdjustedCustomerInfoInputs,
      getAdjustedShares,
      formatClickRatePercent,
      calculateSum,
      calculateAverage,
      calculateSumClicks,
      calculateAverageClicks,
      calculateClickRateFromSum,
      calculateConversionRateFromSum,
      calculateAdjustedHospitalSelectionsSum,
      calculateAdjustedCustomerInfoInputsSum,
      calculateAdjustedSharesSum,
    };
  },
};
</script>

<style scoped>
.message-conversion-report {
  padding: 0;
  background: transparent;
}

/* 메시지 유형별 전환 하위 탭 */
.message-conversion-sub-tabs {
  margin-top: 0;
  padding-top: 16px;
}

:deep(.message-conversion-sub-tabs .ant-tabs-nav) {
  margin-bottom: 20px;
  background: #fafafa;
  padding: 12px 16px 0;
  border-radius: 8px 8px 0 0;
}

:deep(.message-conversion-sub-tabs .ant-tabs-tab) {
  padding: 10px 20px;
  font-weight: 500;
}

:deep(.message-conversion-sub-tabs .ant-tabs-tab-active) {
  background: #fff;
  border-radius: 6px 6px 0 0;
}

:deep(.ant-table-wrapper) {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
  border-bottom: 2px solid #e8e8e8;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

.link-text {
  color: #1890ff;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;
  transition: all 0.2s ease;
}

.link-text:hover {
  color: #40a9ff;
  text-decoration-thickness: 2px;
}

.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

.reservation-highlight {
  color: #f5222d !important;
  font-weight: 700 !important;
  background: #fff1f0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Summary 행 스타일 */
:deep(.summary-row) {
  background: #e6f7ff !important;
  border-top: 2px solid #1890ff;
}

:deep(.summary-row td) {
  font-weight: 600 !important;
  color: #0050b3 !important;
}
</style>
